import { useMemo, useState } from "react";
import { Card, Input, Select } from "antd";
import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AUDIT_FLOW, AUDIT_ORIGINS, AUDIT_TYPES } from "@audit-plan/shared";
import { download } from "../api/client";
import { useAuditClients, useAudits, useSuppliersList } from "../api/audits";
import { usePeople } from "../api/people";
import { useAuth } from "../auth/AuthContext";
import { PageHead } from "../components/audit/PageHead";
import { AuditTable } from "../components/audit/AuditTable";
import { AuditFormModal } from "../components/audit/AuditFormModal";
import { FilterBar, PageActions } from "../components/FilterBar";

export default function AuditsPage() {
  const { t } = useTranslation();
  const { me, can } = useAuth();
  const nav = useNavigate();
  const people = usePeople();
  const isSup = me?.role === "supplier";
  const [status, setStatus] = useState<string>();
  const [type, setType] = useState<string>();
  const [supplierId, setSupplierId] = useState<string>();
  const [auditorId, setAuditorId] = useState<string>();
  const [origin, setOrigin] = useState<string>();
  const [client, setClient] = useState<string>();
  const clients = useAuditClients();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const suppliers = useSuppliersList(); // già filtrato dal server per il ruolo
  const list = useAudits({ status, type, supplierId, auditorId, origin, client: origin === "program" ? undefined : client, q: q.trim() || undefined });
  const rows = useMemo(() => [...(list.data ?? [])].sort((a, b) => ((b.plannedDate ?? b.createdAt) > (a.plannedDate ?? a.createdAt) ? 1 : -1)), [list.data]);
  return (
    <div>
      <PageHead
        title={t("audits")}
        extra={
          <PageActions
            items={[
              { key: "new", label: t("newAudit"), icon: <PlusOutlined />, primary: true, hidden: !can("audit.create"), onClick: () => setOpen(true) },
              { key: "xlsx", label: t("exportXlsx"), icon: <DownloadOutlined />, onClick: () => void download("/exports/audits.xlsx") },
            ]}
          />
        }
      />
      <FilterBar
        active={[status, type, supplierId, auditorId, origin, client].filter(Boolean).length}
        onReset={() => { setStatus(undefined); setType(undefined); setSupplierId(undefined); setAuditorId(undefined); setOrigin(undefined); setClient(undefined); setQ(""); }}
        search={<Input.Search allowClear placeholder={t("search")} style={{ width: 280, maxWidth: "100%" }} onSearch={setQ} onChange={(e) => !e.target.value && setQ("")} />}
      >
        <Select allowClear placeholder={t("status")} style={{ width: 170 }} value={status} onChange={setStatus} options={AUDIT_FLOW.map((s) => ({ value: s, label: t(`st_${s}`) }))} />
        <Select allowClear placeholder={t("auditType")} style={{ width: 170 }} value={type} onChange={setType} options={AUDIT_TYPES.map((s) => ({ value: s, label: t(`t_${s}`) }))} />
        <Select allowClear placeholder={t("c_origin")} style={{ width: 180 }} value={origin} onChange={(v) => { setOrigin(v); if (v === "program") setClient(undefined); }} options={AUDIT_ORIGINS.map((s) => ({ value: s, label: t(`c_origin_${s}`) }))} />
        {origin === "program" ? null : <Select allowClear showSearch optionFilterProp="label" placeholder={t("c_client")} style={{ width: 160 }} value={client} onChange={setClient} options={clients.map((c) => ({ value: c, label: c }))} />}
        {isSup ? null : <Select allowClear showSearch optionFilterProp="label" placeholder={t("supplier")} style={{ width: 220 }} value={supplierId} onChange={setSupplierId} options={(suppliers.data ?? []).map((s) => ({ value: s.id, label: s.name }))} />}
        {isSup ? null : <Select allowClear showSearch optionFilterProp="label" placeholder={t("auditor")} style={{ width: 180 }} value={auditorId} onChange={setAuditorId} options={people.options(["auditor"])} />}
      </FilterBar>
      <Card styles={{ body: { padding: 0 } }}>
        <AuditTable audits={rows} withSupplier={!isSup} loading={list.isLoading} pageSizeKey="audits" />
      </Card>
      <AuditFormModal open={open} onClose={() => setOpen(false)} onSaved={(a) => a?.id && nav(`/audits/${a.id}`)} />
    </div>
  );
}
